import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, LogOut, User, Crown, ChevronDown, ChevronRight, Plus, X, Search, Grid, List, MoreVertical, Edit2, Trash2, MapPin, Calendar, Clock, Check, Camera, DollarSign, Heart, Users as UsersIcon, Package, FileText, Download, Upload, CheckCircle, Circle, Image as ImageIcon, Plane, Hotel, Utensils, ShoppingBag, AlertCircle, Eye } from 'lucide-react';
import { fetchTrips, createTrip, updateTrip, deleteTrip } from '../services/tripsApi';
import MemberAvatar from './MemberAvatar';
import UserAvatar from './UserAvatar';
import MemberSelector from './MemberSelector';
import Premium from './Premium';

const Trips = () => {
    const navigate = useNavigate();
    const [showFamilyDropdown, setShowFamilyDropdown] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const [families, setFamilies] = useState([]);
    const [selectedFamily, setSelectedFamily] = useState(null);
    const [viewMode, setViewMode] = useState('grid'); // grid or list
    const [showTripModal, setShowTripModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedTrip, setSelectedTrip] = useState(null);
    const [activeTab, setActiveTab] = useState('itinerary'); // itinerary, packing, wishlist, expenses, gallery
    const [filterStatus, setFilterStatus] = useState('all'); // all, upcoming, ongoing, completed
    const [searchQuery, setSearchQuery] = useState('');
    const [editingTrip, setEditingTrip] = useState(null);
    const [openMenuId, setOpenMenuId] = useState(null);

    // Trip form states
    const [title, setTitle] = useState('');
    const [destination, setDestination] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [coverPhoto, setCoverPhoto] = useState(null);
    const [coverPhotoPreview, setCoverPhotoPreview] = useState(null);
    const [description, setDescription] = useState('');
    const [status, setStatus] = useState('upcoming'); // upcoming, ongoing, completed

    // Itinerary states
    const [showAddItineraryItem, setShowAddItineraryItem] = useState(false);
    const [itineraryDay, setItineraryDay] = useState('');
    const [itineraryTitle, setItineraryTitle] = useState('');
    const [itineraryTime, setItineraryTime] = useState('');
    const [itineraryActivity, setItineraryActivity] = useState('');
    const [itineraryNotes, setItineraryNotes] = useState('');
    const [editingItinerary, setEditingItinerary] = useState(null);

    // Packing list states
    const [showAddPackingItem, setShowAddPackingItem] = useState(false);
    const [packingCategory, setPackingCategory] = useState('Clothes');
    const [packingItem, setPackingItem] = useState('');
    const [packingAssignedTo, setPackingAssignedTo] = useState('');
    const [editingPackingItem, setEditingPackingItem] = useState(null);

    // Wishlist states
    const [showAddWishlistItem, setShowAddWishlistItem] = useState(false);
    const [wishlistPlace, setWishlistPlace] = useState('');
    const [wishlistType, setWishlistType] = useState('Adventure');
    const [wishlistNotes, setWishlistNotes] = useState('');
    const [editingWishlistItem, setEditingWishlistItem] = useState(null);

    // Expense states
    const [showAddExpense, setShowAddExpense] = useState(false);
    const [expenseDescription, setExpenseDescription] = useState('');
    const [expenseAmount, setExpenseAmount] = useState('');
    const [expenseCategory, setExpenseCategory] = useState('Travel');
    const [expensePaidBy, setExpensePaidBy] = useState('');
    const [expenseSplitEqually, setExpenseSplitEqually] = useState(true);
    const [expenseSplitWith, setExpenseSplitWith] = useState([]);
    const [expenseDate, setExpenseDate] = useState('');
    const [editingExpense, setEditingExpense] = useState(null);

    // Gallery states
    const [showAddPhoto, setShowAddPhoto] = useState(false);
    const [galleryPhoto, setGalleryPhoto] = useState(null);
    const [galleryPhotoPreview, setGalleryPhotoPreview] = useState(null);
    const [galleryCaption, setGalleryCaption] = useState('');

    // Trips data
    const [trips, setTrips] = useState([]);

    const dropdownRef = useRef(null);
    const userMenuRef = useRef(null);
    const coverPhotoInputRef = useRef(null);
    const galleryPhotoInputRef = useRef(null);

    const packingCategories = ['Clothes', 'Documents', 'Essentials', 'Kids', 'Gadgets', 'Toiletries', 'Medications', 'Other'];
    const wishlistTypes = ['Adventure', 'Cultural', 'Food', 'Relaxation', 'Shopping', 'Nature', 'Entertainment', 'Other'];
    const expenseCategories = ['Travel', 'Stay', 'Food', 'Shopping', 'Activities', 'Transportation', 'Misc'];

    useEffect(() => {
        const loadData = async () => {
            try {
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
                const response = await fetch(`${apiUrl}/v1/families`, {
                    credentials: 'include'
                });

                if (!response.ok) {
                    throw new Error('Failed to load families');
                }

                const familiesData = await response.json();
                setFamilies(familiesData);

                const selectedFamilyId = localStorage.getItem('selectedFamilyId');
                let familyToSelect = null;

                if (selectedFamilyId && familiesData.length > 0) {
                    familyToSelect = familiesData.find(f => f.id === selectedFamilyId);
                }

                if (!familyToSelect && familiesData.length === 1) {
                    familyToSelect = familiesData[0];
                    localStorage.setItem('selectedFamilyId', familiesData[0].id);
                }

                if (!familyToSelect && familiesData.length > 0) {
                    localStorage.removeItem('selectedFamilyId');
                }

                setSelectedFamily(familyToSelect);
            } catch (error) {
                console.error('Error loading families:', error);
            }
        };
        loadData();
    }, []);

    // Load trips when family changes
    useEffect(() => {
        const loadTripsData = async () => {
            if (!selectedFamily) return;

            try {
                const response = await fetchTrips(selectedFamily.id);
                setTrips(response.items || []);
            } catch (error) {
                console.error('Error loading trips:', error);
            }
        };
        loadTripsData();
    }, [selectedFamily]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowFamilyDropdown(false);
            }
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (showTripModal || showDetailModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
    }, [showTripModal, showDetailModal]);

    const handleLogout = () => {
        localStorage.removeItem('hasLoggedIn');
        localStorage.removeItem('selectedFamilyId');
        navigate('/login');
    };

    const getFamilyMembers = () => {
        if (!selectedFamily || !selectedFamily.members) return [];
        return selectedFamily.members.filter(member => member && typeof member === 'object' && member.name);
    };

    const handleCoverPhotoSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCoverPhoto(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setCoverPhotoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleGalleryPhotoSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            setGalleryPhoto(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setGalleryPhotoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const resetTripForm = () => {
        setTitle('');
        setDestination('');
        setStartDate('');
        setEndDate('');
        setCoverPhoto(null);
        setCoverPhotoPreview(null);
        setDescription('');
        setStatus('upcoming');
        setEditingTrip(null);
        setShowTripModal(false);
        if (coverPhotoInputRef.current) {
            coverPhotoInputRef.current.value = '';
        }
    };

    const handleSaveTrip = async () => {
        if (!title || !destination || !startDate || !endDate) {
            alert('Please fill in all required fields');
            return;
        }

        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            // Map frontend fields to backend schema
            const tripData = {
                title: title,
                destination: destination,
                start_date: startDate,
                end_date: endDate,
                cover_photo_url: coverPhotoPreview || null,
                description: description || null,
                status: status,
                itinerary: editingTrip?.itinerary || [],
                packing_list: editingTrip?.packingList || [],
                wishlist: editingTrip?.wishlist || [],
                expenses: editingTrip?.expenses || [],
                gallery: editingTrip?.gallery || []
            };

            if (editingTrip) {
                const updated = await updateTrip(selectedFamily.id, editingTrip.id, tripData);
                setTrips(trips.map(t => t.id === editingTrip.id ? updated : t));
            } else {
                const created = await createTrip(selectedFamily.id, tripData);
                setTrips([...trips, created]);
            }

            resetTripForm();
        } catch (error) {
            console.error('Error saving trip:', error);
            alert(`Failed to save trip: ${error.message}`);
        }
    };

    const handleEditTrip = (trip) => {
        setEditingTrip(trip);
        setTitle(trip.title);
        setDestination(trip.destination);
        setStartDate(trip.startDate);
        setEndDate(trip.endDate);
        setCoverPhotoPreview(trip.coverPhoto);
        setDescription(trip.description || '');
        setStatus(trip.status);
        setShowTripModal(true);
        setOpenMenuId(null);
    };

    const handleDeleteTrip = async (id) => {
        if (!window.confirm('Are you sure you want to delete this trip?')) {
            return;
        }

        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            await deleteTrip(selectedFamily.id, id);
            setTrips(trips.filter(t => t.id !== id));
        } catch (error) {
            console.error('Error deleting trip:', error);
            alert(`Failed to delete trip: ${error.message}`);
        }

        setOpenMenuId(null);
    };

    const handleViewTrip = (trip) => {
        setSelectedTrip(trip);
        setShowDetailModal(true);
        setActiveTab('itinerary');
        setOpenMenuId(null);
    };

    const handleMarkAsCompleted = (trip) => {
        setTrips(trips.map(t => t.id === trip.id ? { ...t, status: 'completed' } : t));
        if (selectedTrip?.id === trip.id) {
            setSelectedTrip({ ...trip, status: 'completed' });
        }
        setOpenMenuId(null);
    };

    // Itinerary functions
    const handleSaveItineraryItem = () => {
        if (!itineraryDay || !itineraryTitle) {
            alert('Please fill in day and title');
            return;
        }

        const item = {
            id: editingItinerary?.id || Date.now(),
            day: itineraryDay,
            title: itineraryTitle,
            time: itineraryTime,
            activity: itineraryActivity,
            notes: itineraryNotes
        };

        const updatedTrip = {
            ...selectedTrip,
            itinerary: editingItinerary
                ? selectedTrip.itinerary.map(i => i.id === editingItinerary.id ? item : i)
                : [...selectedTrip.itinerary, item]
        };

        setTrips(trips.map(t => t.id === selectedTrip.id ? updatedTrip : t));
        setSelectedTrip(updatedTrip);

        setItineraryDay('');
        setItineraryTitle('');
        setItineraryTime('');
        setItineraryActivity('');
        setItineraryNotes('');
        setEditingItinerary(null);
        setShowAddItineraryItem(false);
    };

    const handleDeleteItineraryItem = (itemId) => {
        const updatedTrip = {
            ...selectedTrip,
            itinerary: selectedTrip.itinerary.filter(i => i.id !== itemId)
        };
        setTrips(trips.map(t => t.id === selectedTrip.id ? updatedTrip : t));
        setSelectedTrip(updatedTrip);
    };

    // Packing list functions
    const handleSavePackingItem = () => {
        if (!packingItem) {
            alert('Please enter item name');
            return;
        }

        const item = {
            id: editingPackingItem?.id || Date.now(),
            category: packingCategory,
            item: packingItem,
            assignedTo: packingAssignedTo,
            packed: editingPackingItem?.packed || false
        };

        const updatedTrip = {
            ...selectedTrip,
            packingList: editingPackingItem
                ? selectedTrip.packingList.map(i => i.id === editingPackingItem.id ? item : i)
                : [...selectedTrip.packingList, item]
        };

        setTrips(trips.map(t => t.id === selectedTrip.id ? updatedTrip : t));
        setSelectedTrip(updatedTrip);

        setPackingCategory('Clothes');
        setPackingItem('');
        setPackingAssignedTo('');
        setEditingPackingItem(null);
        setShowAddPackingItem(false);
    };

    const handleTogglePacked = (itemId) => {
        const updatedTrip = {
            ...selectedTrip,
            packingList: selectedTrip.packingList.map(i =>
                i.id === itemId ? { ...i, packed: !i.packed } : i
            )
        };
        setTrips(trips.map(t => t.id === selectedTrip.id ? updatedTrip : t));
        setSelectedTrip(updatedTrip);
    };

    const handleDeletePackingItem = (itemId) => {
        const updatedTrip = {
            ...selectedTrip,
            packingList: selectedTrip.packingList.filter(i => i.id !== itemId)
        };
        setTrips(trips.map(t => t.id === selectedTrip.id ? updatedTrip : t));
        setSelectedTrip(updatedTrip);
    };

    // Wishlist functions
    const handleSaveWishlistItem = () => {
        if (!wishlistPlace) {
            alert('Please enter place/activity name');
            return;
        }

        const item = {
            id: editingWishlistItem?.id || Date.now(),
            place: wishlistPlace,
            type: wishlistType,
            notes: wishlistNotes,
            hearts: editingWishlistItem?.hearts || 0,
            heartedBy: editingWishlistItem?.heartedBy || []
        };

        const updatedTrip = {
            ...selectedTrip,
            wishlist: editingWishlistItem
                ? selectedTrip.wishlist.map(i => i.id === editingWishlistItem.id ? item : i)
                : [...selectedTrip.wishlist, item]
        };

        setTrips(trips.map(t => t.id === selectedTrip.id ? updatedTrip : t));
        setSelectedTrip(updatedTrip);

        setWishlistPlace('');
        setWishlistType('Adventure');
        setWishlistNotes('');
        setEditingWishlistItem(null);
        setShowAddWishlistItem(false);
    };

    const handleToggleHeart = (itemId) => {
        const currentUser = selectedFamily?.members?.[0] || 'You';
        const updatedTrip = {
            ...selectedTrip,
            wishlist: selectedTrip.wishlist.map(i => {
                if (i.id === itemId) {
                    const heartedBy = i.heartedBy || [];
                    const hasHearted = heartedBy.includes(currentUser);
                    return {
                        ...i,
                        hearts: hasHearted ? i.hearts - 1 : i.hearts + 1,
                        heartedBy: hasHearted
                            ? heartedBy.filter(u => u !== currentUser)
                            : [...heartedBy, currentUser]
                    };
                }
                return i;
            })
        };
        setTrips(trips.map(t => t.id === selectedTrip.id ? updatedTrip : t));
        setSelectedTrip(updatedTrip);
    };

    const handleDeleteWishlistItem = (itemId) => {
        const updatedTrip = {
            ...selectedTrip,
            wishlist: selectedTrip.wishlist.filter(i => i.id !== itemId)
        };
        setTrips(trips.map(t => t.id === selectedTrip.id ? updatedTrip : t));
        setSelectedTrip(updatedTrip);
    };

    // Expense functions
    const handleSaveExpense = () => {
        if (!expenseDescription || !expenseAmount || !expensePaidBy) {
            alert('Please fill in all required fields');
            return;
        }

        const members = expenseSplitEqually ? getFamilyMembers() : expenseSplitWith;
        const splitAmount = members.length > 0 ? parseFloat(expenseAmount) / members.length : parseFloat(expenseAmount);

        const item = {
            id: editingExpense?.id || Date.now(),
            description: expenseDescription,
            amount: parseFloat(expenseAmount),
            category: expenseCategory,
            paidBy: expensePaidBy,
            splitEqually: expenseSplitEqually,
            splitWith: members,
            splitAmount: splitAmount.toFixed(2),
            date: expenseDate || new Date().toISOString().split('T')[0]
        };

        const updatedTrip = {
            ...selectedTrip,
            expenses: editingExpense
                ? selectedTrip.expenses.map(i => i.id === editingExpense.id ? item : i)
                : [...selectedTrip.expenses, item]
        };

        setTrips(trips.map(t => t.id === selectedTrip.id ? updatedTrip : t));
        setSelectedTrip(updatedTrip);

        setExpenseDescription('');
        setExpenseAmount('');
        setExpenseCategory('Travel');
        setExpensePaidBy('');
        setExpenseSplitEqually(true);
        setExpenseSplitWith([]);
        setExpenseDate('');
        setEditingExpense(null);
        setShowAddExpense(false);
    };

    const handleDeleteExpense = (itemId) => {
        const updatedTrip = {
            ...selectedTrip,
            expenses: selectedTrip.expenses.filter(i => i.id !== itemId)
        };
        setTrips(trips.map(t => t.id === selectedTrip.id ? updatedTrip : t));
        setSelectedTrip(updatedTrip);
    };

    // Gallery functions
    const handleSaveGalleryPhoto = () => {
        if (!galleryPhoto) {
            alert('Please select a photo');
            return;
        }

        const item = {
            id: Date.now(),
            photo: galleryPhotoPreview,
            caption: galleryCaption,
            uploadedBy: selectedFamily?.members?.[0] || 'You',
            uploadedAt: new Date().toISOString()
        };

        const updatedTrip = {
            ...selectedTrip,
            gallery: [...selectedTrip.gallery, item]
        };

        setTrips(trips.map(t => t.id === selectedTrip.id ? updatedTrip : t));
        setSelectedTrip(updatedTrip);

        setGalleryPhoto(null);
        setGalleryPhotoPreview(null);
        setGalleryCaption('');
        setShowAddPhoto(false);
        if (galleryPhotoInputRef.current) {
            galleryPhotoInputRef.current.value = '';
        }
    };

    const handleDeleteGalleryPhoto = (itemId) => {
        const updatedTrip = {
            ...selectedTrip,
            gallery: selectedTrip.gallery.filter(i => i.id !== itemId)
        };
        setTrips(trips.map(t => t.id === selectedTrip.id ? updatedTrip : t));
        setSelectedTrip(updatedTrip);
    };

    const getFilteredTrips = () => {
        let filtered = trips;

        if (filterStatus !== 'all') {
            filtered = filtered.filter(trip => trip.status === filterStatus);
        }

        if (searchQuery) {
            filtered = filtered.filter(trip =>
                trip.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                trip.destination.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        return filtered.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
    };

    const getPackingProgress = (packingList) => {
        if (!packingList || packingList.length === 0) return 0;
        const packed = packingList.filter(item => item.packed).length;
        return Math.round((packed / packingList.length) * 100);
    };

    const getTotalExpenses = (expenses) => {
        if (!expenses || expenses.length === 0) return 0;
        return expenses.reduce((sum, exp) => sum + exp.amount, 0).toFixed(2);
    };

    const getStatusBadge = (status) => {
        const badges = {
            upcoming: 'bg-blue-100 text-blue-700',
            ongoing: 'bg-green-100 text-green-700',
            completed: 'bg-gray-100 text-gray-700'
        };
        return badges[status] || badges.upcoming;
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <nav className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
                            >
                                <img src="/logo.png" alt="mifamli logo" className="h-10 w-10" />
                                <span className="text-xl font-bold text-primary-blue">mifamli</span>
                            </button>

                            {/* Mi Famlis Dropdown */}
                            <div className="relative" ref={dropdownRef}>
                                <button
                                    onClick={() => setShowFamilyDropdown(!showFamilyDropdown)}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white shadow-md hover:shadow-lg transition-all"
                                    style={{
                                        background: 'linear-gradient(135deg, #3b82f6 0%, #ec4899 100%)'
                                    }}
                                >
                                    {selectedFamily ? (
                                        <>
                                            <img
                                                src={selectedFamily.photo}
                                                alt={selectedFamily.name}
                                                className="w-6 h-6 rounded-full object-cover"
                                            />
                                            <span>{selectedFamily.name}</span>
                                        </>
                                    ) : (
                                        <span>Mi Famlis</span>
                                    )}
                                    <ChevronDown className={`w-4 h-4 transition-transform ${showFamilyDropdown ? 'rotate-180' : ''}`} />
                                </button>

                                {/* Dropdown Menu */}
                                {showFamilyDropdown && (
                                    <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                                        {families.length > 0 ? (
                                            <>
                                                {families.map((family) => (
                                                    <button
                                                        key={family.id}
                                                        onClick={() => {
                                                            setSelectedFamily(family);
                                                            localStorage.setItem('selectedFamilyId', family.id.toString());
                                                            setShowFamilyDropdown(false);
                                                        }}
                                                        className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 ${selectedFamily?.id === family.id ? 'bg-blue-50' : ''
                                                            }`}
                                                    >
                                                        <img
                                                            src={family.photo}
                                                            alt={family.name}
                                                            className="w-10 h-10 rounded-full object-cover"
                                                        />
                                                        <div className="flex-1">
                                                            <p className="font-semibold text-gray-800">{family.name}</p>
                                                            {family.tagline && (
                                                                <p className="text-xs text-gray-500 italic truncate">"{family.tagline}"</p>
                                                            )}
                                                        </div>
                                                    </button>
                                                ))}
                                                <div className="border-t border-gray-200 my-2"></div>
                                            </>
                                        ) : (
                                            <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                                No families yet
                                            </div>
                                        )}
                                        <button
                                            onClick={() => {
                                                setShowFamilyDropdown(false);
                                                navigate('/famlis');
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-primary-blue/5 flex items-center gap-3 text-primary-blue font-semibold"
                                        >
                                            <Plus className="w-5 h-5" />
                                            <span>Add Family</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-4">
                            {/* Premium Button */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowPremiumModal(true)}
                                    className="relative px-4 py-2 rounded-lg font-semibold text-white hover:scale-105 transform transition-all duration-200 backdrop-blur-md border-2 border-white/50 overflow-hidden group"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.85) 0%, rgba(236, 72, 153, 0.85) 100%)',
                                        boxShadow: '0 0 20px rgba(59, 130, 246, 0.6), 0 0 40px rgba(236, 72, 153, 0.6)'
                                    }}
                                >
                                    <span className="relative z-10 flex items-center gap-2">
                                        <span className="hidden sm:inline">Go Premium</span>
                                        <span className="sm:hidden">Premium</span>
                                    </span>
                                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/50 to-pink-500/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                                </button>

                                {/* Golden Crown Overlay */}
                                <div className="absolute -top-1 -right-1 transform rotate-12">
                                    <Crown className="w-5 h-5 text-yellow-500 fill-yellow-400 drop-shadow-lg" />
                                </div>
                            </div>

                            {/* User Avatar Menu */}
                            <div className="relative" ref={userMenuRef}>
                                <button
                                    onClick={() => setShowUserMenu(!showUserMenu)}
                                    className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                                >
                                    <UserAvatar size="medium" />
                                </button>

                                {/* User Dropdown Menu */}
                                {showUserMenu && (
                                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                                        <div className="px-4 py-3 border-b border-gray-200">
                                            <p className="text-sm font-semibold text-gray-800">User Account</p>
                                            <p className="text-xs text-gray-500">Manage your profile</p>
                                        </div>
                                        <button
                                            onClick={() => {
                                                setShowUserMenu(false);
                                                alert('Profile settings coming soon!');
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        >
                                            <User className="w-5 h-5 text-gray-600" />
                                            <span>Profile</span>
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowUserMenu(false);
                                                alert('Settings coming soon!');
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        >
                                            <Settings className="w-5 h-5 text-gray-600" />
                                            <span>Settings</span>
                                        </button>
                                        <div className="border-t border-gray-200 my-2"></div>
                                        <button
                                            onClick={() => {
                                                setShowUserMenu(false);
                                                handleLogout();
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                                        >
                                            <LogOut className="w-5 h-5" />
                                            <span>Logout</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Breadcrumb */}
                <div className="mb-4 flex items-center gap-2 text-sm">
                    <button 
                        onClick={() => navigate('/dashboard')}
                        className="text-gray-600 hover:text-primary-blue transition-colors"
                    >
                        Dashboard
                    </button>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900 font-medium">Travel & Vacations</span>
                </div>

                {/* Header */}
                <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                        <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                            <Plane className="w-8 h-8 text-blue-500" />
                            Travel & Vacations
                        </h2>
                        <p className="text-gray-600 mt-1">Plan and manage your family trips</p>
                    </div>
                    <button
                        onClick={() => setShowTripModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                        <Plus className="w-5 h-5" />
                        Create Trip
                    </button>
                </div>

                {/* Filters */}
                <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <button
                            onClick={() => setFilterStatus('all')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${filterStatus === 'all'
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                        >
                            All Trips
                        </button>
                        <button
                            onClick={() => setFilterStatus('upcoming')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${filterStatus === 'upcoming'
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                        >
                            Upcoming
                        </button>
                        <button
                            onClick={() => setFilterStatus('ongoing')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${filterStatus === 'ongoing'
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                        >
                            Ongoing
                        </button>
                        <button
                            onClick={() => setFilterStatus('completed')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${filterStatus === 'completed'
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                        >
                            Completed
                        </button>
                    </div>

                    <div className="flex items-center gap-3 w-full sm:w-auto">
                        <div className="relative flex-1 sm:flex-initial">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search trips..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full sm:w-64"
                            />
                        </div>

                        <div className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg p-1">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
                            >
                                <Grid className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-2 rounded ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
                            >
                                <List className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Trips List */}
                {getFilteredTrips().length === 0 ? (
                    <div className="bg-white rounded-xl shadow-md p-12 text-center">
                        <Plane className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500 mb-4">No trips yet. Start planning your next adventure!</p>
                        <button
                            onClick={() => setShowTripModal(true)}
                            className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                        >
                            Create Your First Trip
                        </button>
                    </div>
                ) : (
                    <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
                        {getFilteredTrips().map((trip) => (
                            viewMode === 'grid' ? (
                                // Grid View
                                <div key={trip.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow relative group">
                                    {/* Cover Photo */}
                                    <div className="relative h-48 bg-gradient-to-br from-blue-400 to-purple-500">
                                        {trip.coverPhoto ? (
                                            <img src={trip.coverPhoto} alt={trip.title} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <Plane className="w-16 h-16 text-white opacity-50" />
                                            </div>
                                        )}

                                        {/* Status Badge */}
                                        <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(trip.status)}`}>
                                            {trip.status.charAt(0).toUpperCase() + trip.status.slice(1)}
                                        </div>

                                        {/* Menu */}
                                        <div className="absolute top-3 right-3">
                                            <button
                                                onClick={() => setOpenMenuId(openMenuId === trip.id ? null : trip.id)}
                                                className="p-2 bg-white/90 backdrop-blur-sm rounded-lg hover:bg-white transition-colors"
                                            >
                                                <MoreVertical className="w-5 h-5 text-gray-600" />
                                            </button>

                                            {openMenuId === trip.id && (
                                                <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-10">
                                                    <button
                                                        onClick={() => handleViewTrip(trip)}
                                                        className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                        <span>View Details</span>
                                                    </button>
                                                    <button
                                                        onClick={() => handleEditTrip(trip)}
                                                        className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                        <span>Edit</span>
                                                    </button>
                                                    {trip.status !== 'completed' && (
                                                        <button
                                                            onClick={() => handleMarkAsCompleted(trip)}
                                                            className="w-full px-4 py-2 text-left hover:bg-green-50 flex items-center gap-3 text-green-600"
                                                        >
                                                            <CheckCircle className="w-4 h-4" />
                                                            <span>Mark as Completed</span>
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleDeleteTrip(trip.id)}
                                                        className="w-full px-4 py-2 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                        <span>Delete</span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Info */}
                                    <div className="p-4 cursor-pointer" onClick={() => handleViewTrip(trip)}>
                                        <h3 className="font-bold text-lg text-gray-900 mb-1">{trip.title}</h3>
                                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                                            <MapPin className="w-4 h-4" />
                                            <span>{trip.destination}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                                            <Calendar className="w-4 h-4" />
                                            <span>{new Date(trip.startDate).toLocaleDateString()} - {new Date(trip.endDate).toLocaleDateString()}</span>
                                        </div>

                                        {/* Quick Stats */}
                                        <div className="grid grid-cols-2 gap-2 pt-3 border-t border-gray-200">
                                            <div className="text-center">
                                                <div className="text-xs text-gray-500">Itinerary</div>
                                                <div className="text-lg font-bold text-gray-900">{trip.itinerary?.length || 0}</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-xs text-gray-500">Photos</div>
                                                <div className="text-lg font-bold text-gray-900">{trip.gallery?.length || 0}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                // List View
                                <div key={trip.id} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
                                    <div className="flex items-start gap-4">
                                        {/* Cover Photo */}
                                        <div className="w-24 h-24 rounded-lg bg-gradient-to-br from-blue-400 to-purple-500 flex-shrink-0 overflow-hidden">
                                            {trip.coverPhoto ? (
                                                <img src={trip.coverPhoto} alt={trip.title} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <Plane className="w-10 h-10 text-white opacity-50" />
                                                </div>
                                            )}
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => handleViewTrip(trip)}>
                                            <div className="flex items-start justify-between mb-2">
                                                <div className="flex-1">
                                                    <h3 className="text-xl font-bold text-gray-900 mb-1">{trip.title}</h3>
                                                    <div className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(trip.status)}`}>
                                                        {trip.status.charAt(0).toUpperCase() + trip.status.slice(1)}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600 mb-3">
                                                <div className="flex items-center gap-2">
                                                    <MapPin className="w-4 h-4" />
                                                    <span>{trip.destination}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Calendar className="w-4 h-4" />
                                                    <span>{new Date(trip.startDate).toLocaleDateString()} - {new Date(trip.endDate).toLocaleDateString()}</span>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-6 text-sm text-gray-600">
                                                <span>{trip.itinerary?.length || 0} itinerary items</span>
                                                <span>{trip.packingList?.length || 0} packing items</span>
                                                <span>{trip.expenses?.length || 0} expenses</span>
                                                <span>{trip.gallery?.length || 0} photos</span>
                                            </div>
                                        </div>

                                        {/* Menu */}
                                        <div className="relative">
                                            <button
                                                onClick={() => setOpenMenuId(openMenuId === trip.id ? null : trip.id)}
                                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                            >
                                                <MoreVertical className="w-5 h-5 text-gray-600" />
                                            </button>

                                            {openMenuId === trip.id && (
                                                <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-10">
                                                    <button
                                                        onClick={() => handleViewTrip(trip)}
                                                        className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                        <span>View Details</span>
                                                    </button>
                                                    <button
                                                        onClick={() => handleEditTrip(trip)}
                                                        className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                        <span>Edit</span>
                                                    </button>
                                                    {trip.status !== 'completed' && (
                                                        <button
                                                            onClick={() => handleMarkAsCompleted(trip)}
                                                            className="w-full px-4 py-2 text-left hover:bg-green-50 flex items-center gap-3 text-green-600"
                                                        >
                                                            <CheckCircle className="w-4 h-4" />
                                                            <span>Mark as Completed</span>
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleDeleteTrip(trip.id)}
                                                        className="w-full px-4 py-2 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                        <span>Delete</span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )
                        ))}
                    </div>
                )}
            </div>

            {/* Create/Edit Trip Modal */}
            {showTripModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl max-w-2xl w-full my-8">
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
                            <h2 className="text-xl font-bold text-gray-900">
                                {editingTrip ? 'Edit' : 'Create'} Trip
                            </h2>
                            <button
                                onClick={resetTripForm}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 space-y-5 max-h-[calc(100vh-200px)] overflow-y-auto">
                            {/* Cover Photo */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Cover Photo</label>
                                <input
                                    ref={coverPhotoInputRef}
                                    type="file"
                                    onChange={handleCoverPhotoSelect}
                                    accept="image/*"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                {coverPhotoPreview && (
                                    <div className="mt-3">
                                        <img src={coverPhotoPreview} alt="Preview" className="w-full h-48 object-cover rounded-lg border border-gray-200" />
                                    </div>
                                )}
                            </div>

                            {/* Title */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Trip Title *</label>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="e.g., Summer Vacation 2024"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            {/* Destination */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Destination *</label>
                                <input
                                    type="text"
                                    value={destination}
                                    onChange={(e) => setDestination(e.target.value)}
                                    placeholder="e.g., Goa, India"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            {/* Dates */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date *</label>
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date *</label>
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>

                            {/* Status */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                                <select
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="upcoming">Upcoming</option>
                                    <option value="ongoing">Ongoing</option>
                                    <option value="completed">Completed</option>
                                </select>
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Optional description..."
                                    rows="3"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                />
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3 rounded-b-2xl">
                            <button
                                onClick={resetTripForm}
                                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveTrip}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                            >
                                {editingTrip ? 'Update' : 'Create'} Trip
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Trip Detail Modal */}
            {showDetailModal && selectedTrip && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl max-w-6xl w-full my-8">
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900">{selectedTrip.title}</h2>
                                <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                                    <div className="flex items-center gap-1">
                                        <MapPin className="w-4 h-4" />
                                        <span>{selectedTrip.destination}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Calendar className="w-4 h-4" />
                                        <span>{new Date(selectedTrip.startDate).toLocaleDateString()} - {new Date(selectedTrip.endDate).toLocaleDateString()}</span>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    setShowDetailModal(false);
                                    setSelectedTrip(null);
                                }}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Tabs */}
                        <div className="border-b border-gray-200 px-6">
                            <div className="flex gap-4 overflow-x-auto">
                                <button
                                    onClick={() => setActiveTab('itinerary')}
                                    className={`py-3 px-4 font-medium transition-colors whitespace-nowrap ${activeTab === 'itinerary'
                                        ? 'text-blue-500 border-b-2 border-blue-500'
                                        : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                >
                                    <Calendar className="w-4 h-4 inline mr-2" />
                                    Itinerary
                                </button>
                                <button
                                    onClick={() => setActiveTab('packing')}
                                    className={`py-3 px-4 font-medium transition-colors whitespace-nowrap ${activeTab === 'packing'
                                        ? 'text-blue-500 border-b-2 border-blue-500'
                                        : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                >
                                    <Package className="w-4 h-4 inline mr-2" />
                                    Packing List
                                </button>
                                <button
                                    onClick={() => setActiveTab('wishlist')}
                                    className={`py-3 px-4 font-medium transition-colors whitespace-nowrap ${activeTab === 'wishlist'
                                        ? 'text-blue-500 border-b-2 border-blue-500'
                                        : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                >
                                    <MapPin className="w-4 h-4 inline mr-2" />
                                    Wishlist
                                </button>
                                <button
                                    onClick={() => setActiveTab('expenses')}
                                    className={`py-3 px-4 font-medium transition-colors whitespace-nowrap ${activeTab === 'expenses'
                                        ? 'text-blue-500 border-b-2 border-blue-500'
                                        : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                >
                                    <DollarSign className="w-4 h-4 inline mr-2" />
                                    Expenses
                                </button>
                                <button
                                    onClick={() => setActiveTab('gallery')}
                                    className={`py-3 px-4 font-medium transition-colors whitespace-nowrap ${activeTab === 'gallery'
                                        ? 'text-blue-500 border-b-2 border-blue-500'
                                        : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                >
                                    <Camera className="w-4 h-4 inline mr-2" />
                                    Gallery
                                </button>
                            </div>
                        </div>

                        {/* Tab Content */}
                        <div className="p-6 max-h-[calc(100vh-300px)] overflow-y-auto">
                            {/* Itinerary Tab */}
                            {activeTab === 'itinerary' && (
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-bold text-gray-900">Day-by-Day Itinerary</h3>
                                        <button
                                            onClick={() => setShowAddItineraryItem(true)}
                                            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                        >
                                            <Plus className="w-4 h-4" />
                                            Add Item
                                        </button>
                                    </div>

                                    {selectedTrip.itinerary?.length === 0 ? (
                                        <div className="text-center py-12">
                                            <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                            <p className="text-gray-500">No itinerary items yet</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {selectedTrip.itinerary.map((item) => (
                                                <div key={item.id} className="bg-gray-50 rounded-lg p-4">
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-3 mb-2">
                                                                <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full">
                                                                    Day {item.day}
                                                                </span>
                                                                {item.time && (
                                                                    <span className="flex items-center gap-1 text-sm text-gray-600">
                                                                        <Clock className="w-4 h-4" />
                                                                        {item.time}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <h4 className="font-semibold text-gray-900 mb-1">{item.title}</h4>
                                                            {item.activity && (
                                                                <p className="text-sm text-gray-600 mb-2">{item.activity}</p>
                                                            )}
                                                            {item.notes && (
                                                                <p className="text-sm text-gray-500 italic">{item.notes}</p>
                                                            )}
                                                        </div>
                                                        <button
                                                            onClick={() => handleDeleteItineraryItem(item.id)}
                                                            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                                                        >
                                                            <Trash2 className="w-4 h-4 text-red-600" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Add Itinerary Item Modal */}
                                    {showAddItineraryItem && (
                                        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                                            <div className="bg-white rounded-2xl max-w-lg w-full">
                                                <div className="border-b border-gray-200 px-6 py-4">
                                                    <h3 className="text-lg font-bold text-gray-900">Add Itinerary Item</h3>
                                                </div>
                                                <div className="p-6 space-y-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Day *</label>
                                                        <input
                                                            type="number"
                                                            value={itineraryDay}
                                                            onChange={(e) => setItineraryDay(e.target.value)}
                                                            placeholder="e.g., 1"
                                                            min="1"
                                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                                                        <input
                                                            type="text"
                                                            value={itineraryTitle}
                                                            onChange={(e) => setItineraryTitle(e.target.value)}
                                                            placeholder="e.g., Flight + Hotel Check-in"
                                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Time</label>
                                                        <input
                                                            type="time"
                                                            value={itineraryTime}
                                                            onChange={(e) => setItineraryTime(e.target.value)}
                                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Activity</label>
                                                        <input
                                                            type="text"
                                                            value={itineraryActivity}
                                                            onChange={(e) => setItineraryActivity(e.target.value)}
                                                            placeholder="e.g., Sightseeing, Dinner"
                                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                                                        <textarea
                                                            value={itineraryNotes}
                                                            onChange={(e) => setItineraryNotes(e.target.value)}
                                                            placeholder="Additional notes..."
                                                            rows="3"
                                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                                                    <button
                                                        onClick={() => {
                                                            setShowAddItineraryItem(false);
                                                            setItineraryDay('');
                                                            setItineraryTitle('');
                                                            setItineraryTime('');
                                                            setItineraryActivity('');
                                                            setItineraryNotes('');
                                                        }}
                                                        className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        onClick={handleSaveItineraryItem}
                                                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                                    >
                                                        Add
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Packing List Tab */}
                            {activeTab === 'packing' && (
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <div>
                                            <h3 className="text-lg font-bold text-gray-900">Packing List</h3>
                                            {selectedTrip.packingList?.length > 0 && (
                                                <div className="mt-2">
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                                                            <div
                                                                className="bg-green-500 h-2 rounded-full transition-all"
                                                                style={{ width: `${getPackingProgress(selectedTrip.packingList)}%` }}
                                                            ></div>
                                                        </div>
                                                        <span className="text-sm font-medium text-gray-700">
                                                            {getPackingProgress(selectedTrip.packingList)}%
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => setShowAddPackingItem(true)}
                                            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                        >
                                            <Plus className="w-4 h-4" />
                                            Add Item
                                        </button>
                                    </div>

                                    {selectedTrip.packingList?.length === 0 ? (
                                        <div className="text-center py-12">
                                            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                            <p className="text-gray-500">No packing items yet</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {packingCategories.map(cat => {
                                                const items = selectedTrip.packingList.filter(item => item.category === cat);
                                                if (items.length === 0) return null;
                                                return (
                                                    <div key={cat} className="bg-gray-50 rounded-lg p-4">
                                                        <h4 className="font-semibold text-gray-900 mb-3">{cat}</h4>
                                                        <div className="space-y-2">
                                                            {items.map(item => (
                                                                <div key={item.id} className="flex items-center gap-3 bg-white p-3 rounded-lg">
                                                                    <button
                                                                        onClick={() => handleTogglePacked(item.id)}
                                                                        className="flex-shrink-0"
                                                                    >
                                                                        {item.packed ? (
                                                                            <CheckCircle className="w-5 h-5 text-green-500" />
                                                                        ) : (
                                                                            <Circle className="w-5 h-5 text-gray-400" />
                                                                        )}
                                                                    </button>
                                                                    <div className="flex-1">
                                                                        <p className={`font-medium ${item.packed ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                                                                            {item.item}
                                                                        </p>
                                                                        {item.assignedTo && (
                                                                            <div className="flex items-center gap-2 mt-1">
                                                                                <span className="text-xs text-gray-500">Assigned:</span>
                                                                                <MemberAvatar name={item.assignedTo} size="xs" />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <button
                                                                        onClick={() => handleDeletePackingItem(item.id)}
                                                                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                                                    >
                                                                        <Trash2 className="w-4 h-4 text-red-600" />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Add Packing Item Modal */}
                                    {showAddPackingItem && (
                                        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                                            <div className="bg-white rounded-2xl max-w-lg w-full">
                                                <div className="border-b border-gray-200 px-6 py-4">
                                                    <h3 className="text-lg font-bold text-gray-900">Add Packing Item</h3>
                                                </div>
                                                <div className="p-6 space-y-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                                                        <select
                                                            value={packingCategory}
                                                            onChange={(e) => setPackingCategory(e.target.value)}
                                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        >
                                                            {packingCategories.map(cat => (
                                                                <option key={cat} value={cat}>{cat}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Item *</label>
                                                        <input
                                                            type="text"
                                                            value={packingItem}
                                                            onChange={(e) => setPackingItem(e.target.value)}
                                                            placeholder="e.g., T-shirts, Passport"
                                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Assign To</label>
                                                        <select
                                                            value={packingAssignedTo}
                                                            onChange={(e) => setPackingAssignedTo(e.target.value)}
                                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        >
                                                            <option value="">Not assigned</option>
                                                            {getFamilyMembers().map((member, idx) => (
                                                                <option key={idx} value={member.name}>{member.name}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                                                    <button
                                                        onClick={() => {
                                                            setShowAddPackingItem(false);
                                                            setPackingCategory('Clothes');
                                                            setPackingItem('');
                                                            setPackingAssignedTo('');
                                                        }}
                                                        className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        onClick={handleSavePackingItem}
                                                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                                    >
                                                        Add
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Wishlist Tab */}
                            {activeTab === 'wishlist' && (
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-bold text-gray-900">Wishlist</h3>
                                        <button
                                            onClick={() => setShowAddWishlistItem(true)}
                                            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                        >
                                            <Plus className="w-4 h-4" />
                                            Add Place
                                        </button>
                                    </div>

                                    {selectedTrip.wishlist?.length === 0 ? (
                                        <div className="text-center py-12">
                                            <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                            <p className="text-gray-500">No wishlist items yet</p>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {selectedTrip.wishlist.map((item) => (
                                                <div key={item.id} className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-4">
                                                    <div className="flex items-start justify-between mb-2">
                                                        <div className="flex-1">
                                                            <h4 className="font-semibold text-gray-900 mb-1">{item.place}</h4>
                                                            <span className="inline-block px-2 py-1 bg-white text-xs font-medium text-gray-700 rounded-full">
                                                                {item.type}
                                                            </span>
                                                        </div>
                                                        <button
                                                            onClick={() => handleDeleteWishlistItem(item.id)}
                                                            className="p-2 hover:bg-white/50 rounded-lg transition-colors"
                                                        >
                                                            <Trash2 className="w-4 h-4 text-red-600" />
                                                        </button>
                                                    </div>
                                                    {item.notes && (
                                                        <p className="text-sm text-gray-600 mb-3">{item.notes}</p>
                                                    )}
                                                    <button
                                                        onClick={() => handleToggleHeart(item.id)}
                                                        className="flex items-center gap-2 text-sm"
                                                    >
                                                        <Heart
                                                            className={`w-5 h-5 ${item.heartedBy?.includes(selectedFamily?.members?.[0] || 'You')
                                                                ? 'fill-red-500 text-red-500'
                                                                : 'text-gray-400'
                                                                }`}
                                                        />
                                                        <span className="text-gray-600">{item.hearts || 0} {item.hearts === 1 ? 'heart' : 'hearts'}</span>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Add Wishlist Item Modal */}
                                    {showAddWishlistItem && (
                                        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                                            <div className="bg-white rounded-2xl max-w-lg w-full">
                                                <div className="border-b border-gray-200 px-6 py-4">
                                                    <h3 className="text-lg font-bold text-gray-900">Add to Wishlist</h3>
                                                </div>
                                                <div className="p-6 space-y-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Place/Activity *</label>
                                                        <input
                                                            type="text"
                                                            value={wishlistPlace}
                                                            onChange={(e) => setWishlistPlace(e.target.value)}
                                                            placeholder="e.g., Machu Picchu Trek"
                                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                                                        <select
                                                            value={wishlistType}
                                                            onChange={(e) => setWishlistType(e.target.value)}
                                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        >
                                                            {wishlistTypes.map(type => (
                                                                <option key={type} value={type}>{type}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                                                        <textarea
                                                            value={wishlistNotes}
                                                            onChange={(e) => setWishlistNotes(e.target.value)}
                                                            placeholder="Add notes or links..."
                                                            rows="3"
                                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                                                    <button
                                                        onClick={() => {
                                                            setShowAddWishlistItem(false);
                                                            setWishlistPlace('');
                                                            setWishlistType('Adventure');
                                                            setWishlistNotes('');
                                                        }}
                                                        className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        onClick={handleSaveWishlistItem}
                                                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                                    >
                                                        Add
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Expenses Tab */}
                            {activeTab === 'expenses' && (
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <div>
                                            <h3 className="text-lg font-bold text-gray-900">Expenses</h3>
                                            {selectedTrip.expenses?.length > 0 && (
                                                <p className="text-2xl font-bold text-blue-600 mt-1">
                                                    ${getTotalExpenses(selectedTrip.expenses)}
                                                </p>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => setShowAddExpense(true)}
                                            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                        >
                                            <Plus className="w-4 h-4" />
                                            Add Expense
                                        </button>
                                    </div>

                                    {selectedTrip.expenses?.length === 0 ? (
                                        <div className="text-center py-12">
                                            <DollarSign className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                            <p className="text-gray-500">No expenses yet</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {selectedTrip.expenses.map((item) => (
                                                <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-4">
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <h4 className="font-semibold text-gray-900">{item.description}</h4>
                                                                <span className="px-2 py-1 bg-gray-100 text-xs font-medium text-gray-700 rounded-full">
                                                                    {item.category}
                                                                </span>
                                                            </div>
                                                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                                                                <span className="font-bold text-blue-600">${item.amount}</span>
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-xs">Paid:</span>
                                                                    <MemberAvatar name={item.paidBy} size="xs" />
                                                                </div>
                                                                <span>{item.date}</span>
                                                            </div>
                                                            {item.splitWith?.length > 0 && (
                                                                <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                                                                    <span>Split with:</span>
                                                                    <div className="flex -space-x-1">
                                                                        {item.splitWith.map((member, idx) => (
                                                                            <div key={idx} className="relative">
                                                                                <MemberAvatar name={member} size="xs" className="border-2 border-white" />
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                    <span className="text-gray-500">(${item.splitAmount} each)</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <button
                                                            onClick={() => handleDeleteExpense(item.id)}
                                                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                                        >
                                                            <Trash2 className="w-4 h-4 text-red-600" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Add Expense Modal */}
                                    {showAddExpense && (
                                        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                                            <div className="bg-white rounded-2xl max-w-lg w-full">
                                                <div className="border-b border-gray-200 px-6 py-4">
                                                    <h3 className="text-lg font-bold text-gray-900">Add Expense</h3>
                                                </div>
                                                <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                                                        <input
                                                            type="text"
                                                            value={expenseDescription}
                                                            onChange={(e) => setExpenseDescription(e.target.value)}
                                                            placeholder="e.g., Hotel booking"
                                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Amount *</label>
                                                        <input
                                                            type="number"
                                                            value={expenseAmount}
                                                            onChange={(e) => setExpenseAmount(e.target.value)}
                                                            placeholder="0.00"
                                                            step="0.01"
                                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                                                        <select
                                                            value={expenseCategory}
                                                            onChange={(e) => setExpenseCategory(e.target.value)}
                                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        >
                                                            {expenseCategories.map(cat => (
                                                                <option key={cat} value={cat}>{cat}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Paid By *</label>
                                                        <select
                                                            value={expensePaidBy}
                                                            onChange={(e) => setExpensePaidBy(e.target.value)}
                                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        >
                                                            <option value="">Select member</option>
                                                            {getFamilyMembers().map((member, idx) => (
                                                                <option key={idx} value={member.name}>{member.name}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                                                        <input
                                                            type="date"
                                                            value={expenseDate}
                                                            onChange={(e) => setExpenseDate(e.target.value)}
                                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="flex items-center gap-2 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={expenseSplitEqually}
                                                                onChange={(e) => setExpenseSplitEqually(e.target.checked)}
                                                                className="w-5 h-5"
                                                            />
                                                            <span className="text-sm font-medium text-gray-700">Split equally among all members</span>
                                                        </label>
                                                    </div>
                                                </div>
                                                <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                                                    <button
                                                        onClick={() => {
                                                            setShowAddExpense(false);
                                                            setExpenseDescription('');
                                                            setExpenseAmount('');
                                                            setExpenseCategory('Travel');
                                                            setExpensePaidBy('');
                                                            setExpenseSplitEqually(true);
                                                            setExpenseDate('');
                                                        }}
                                                        className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        onClick={handleSaveExpense}
                                                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                                    >
                                                        Add
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Gallery Tab */}
                            {activeTab === 'gallery' && (
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-bold text-gray-900">Photo Gallery</h3>
                                        <button
                                            onClick={() => setShowAddPhoto(true)}
                                            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                        >
                                            <Plus className="w-4 h-4" />
                                            Add Photo
                                        </button>
                                    </div>

                                    {selectedTrip.gallery?.length === 0 ? (
                                        <div className="text-center py-12">
                                            <Camera className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                            <p className="text-gray-500">No photos yet</p>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                            {selectedTrip.gallery.map((item) => (
                                                <div key={item.id} className="relative group">
                                                    <img
                                                        src={item.photo}
                                                        alt={item.caption || 'Trip photo'}
                                                        className="w-full h-48 object-cover rounded-lg"
                                                    />
                                                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                                                        <button
                                                            onClick={() => handleDeleteGalleryPhoto(item.id)}
                                                            className="p-2 bg-white rounded-lg hover:bg-gray-100 transition-colors"
                                                        >
                                                            <Trash2 className="w-5 h-5 text-red-600" />
                                                        </button>
                                                    </div>
                                                    {item.caption && (
                                                        <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-sm p-2 rounded-b-lg">
                                                            {item.caption}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Add Photo Modal */}
                                    {showAddPhoto && (
                                        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                                            <div className="bg-white rounded-2xl max-w-lg w-full">
                                                <div className="border-b border-gray-200 px-6 py-4">
                                                    <h3 className="text-lg font-bold text-gray-900">Add Photo</h3>
                                                </div>
                                                <div className="p-6 space-y-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Select Photo *</label>
                                                        <input
                                                            ref={galleryPhotoInputRef}
                                                            type="file"
                                                            onChange={handleGalleryPhotoSelect}
                                                            accept="image/*"
                                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        />
                                                        {galleryPhotoPreview && (
                                                            <div className="mt-3">
                                                                <img src={galleryPhotoPreview} alt="Preview" className="w-full h-48 object-cover rounded-lg border border-gray-200" />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Caption</label>
                                                        <input
                                                            type="text"
                                                            value={galleryCaption}
                                                            onChange={(e) => setGalleryCaption(e.target.value)}
                                                            placeholder="Optional caption..."
                                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                                                    <button
                                                        onClick={() => {
                                                            setShowAddPhoto(false);
                                                            setGalleryPhoto(null);
                                                            setGalleryPhotoPreview(null);
                                                            setGalleryCaption('');
                                                            if (galleryPhotoInputRef.current) {
                                                                galleryPhotoInputRef.current.value = '';
                                                            }
                                                        }}
                                                        className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        onClick={handleSaveGalleryPhoto}
                                                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                                    >
                                                        Add
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Premium Modal */}
            {showPremiumModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-75">
                    <div className="relative rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" style={{ background: 'transparent' }}>
                        {/* Close Button */}
                        <button
                            onClick={() => setShowPremiumModal(false)}
                            className="absolute top-4 right-4 z-50 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
                        >
                            <X className="w-6 h-6 text-gray-600" />
                        </button>

                        {/* Premium Component */}
                        <Premium isModal={true} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Trips;

